import React, { useState } from 'react';
import { Head, router } from '@inertiajs/react';
import DashboardLayout from '../../../components/DashboardLayout';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Copy, Check, X, Link as LinkIcon, Clock, User, AlertTriangle } from 'lucide-react';

interface InviteToken {
  id: number;
  name: string;
  token: string;
  expires_at: string;
  used: boolean;
  used_at: string | null;
  user_id: number | null;
  created_at: string;
}

interface IndexProps {
  inviteTokens: InviteToken[];
}

const Index: React.FC<IndexProps> = ({ inviteTokens }) => {
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [copyStatus, setCopyStatus] = useState<Record<number, 'idle' | 'success' | 'error'>>({});

  const getStatus = (token: InviteToken): 'active' | 'expired' | 'used' => {
    if (token.used) return 'used';
    const expiresAt = new Date(token.expires_at);
    const now = new Date();
    if (expiresAt.getTime() <= now.getTime()) return 'expired';
    return 'active';
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getDaysUntilExpiry = (expiresAt: string): number => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getBaseUrl = (): string => {
    return window.location.origin;
  };

  const getInviteUrl = (token: string): string => {
    return `${getBaseUrl()}/public/invites/${token}`;
  };

  const copyToClipboard = async (text: string, tokenId: number) => {
    setCopyStatus((prev) => ({ ...prev, [tokenId]: 'idle' }));

    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (!successful) {
          throw new Error('Failed to copy to clipboard');
        }
      }

      setCopyStatus((prev) => ({ ...prev, [tokenId]: 'success' }));

      // Reset status after 3 seconds
      setTimeout(() => {
        setCopyStatus((prev) => ({ ...prev, [tokenId]: 'idle' }));
      }, 3000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      setCopyStatus((prev) => ({ ...prev, [tokenId]: 'error' }));

      // Reset status after 3 seconds
      setTimeout(() => {
        setCopyStatus((prev) => ({ ...prev, [tokenId]: 'idle' }));
      }, 3000);
    }
  };

  const handleCreateToken = () => {
    if (!name.trim()) return;

    setIsSubmitting(true);
    router.post('/invite_tokens', { name }, {
      onFinish: () => {
        setIsSubmitting(false);
        setName('');
      },
    });
  };

  const handleRevokeToken = (tokenId: number) => {
    if (window.confirm('Are you sure you want to revoke this invite link? This action cannot be undone.')) {
      router.delete(`/invite_tokens/${tokenId}`);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isSubmitting) {
      handleCreateToken();
    }
  };

  return (
    <DashboardLayout>
      <Head title="Invite Links" />

      <div className="space-y-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-bold text-foreground">Invite Links</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Create and manage one-time invite links for user registration.
          </p>
        </div>

        {/* Create New Invite Form */}
        <Card className="rounded-xl border bg-card text-card-foreground shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LinkIcon className="h-5 w-5" />
              Create New Invite Link
            </CardTitle>
            <CardDescription>
              Generate a one-time invite link to share with others for user registration.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Label htmlFor="name" className="sr-only">
                  Name
                </Label>
                <Input
                  id="name"
                  placeholder="Enter a name for this invite (e.g., 'Client John Doe')"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyPress={handleKeyPress}
                  disabled={isSubmitting}
                  className="w-full"
                />
              </div>
              <Button
                onClick={handleCreateToken}
                disabled={isSubmitting || !name.trim()}
                className="sm:w-auto w-full"
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                    Generating...
                  </>
                ) : (
                  <>
                    <LinkIcon className="h-4 w-4 mr-2" />
                    Generate Link
                  </>
                )}
              </Button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              The invite link will be valid for 30 days and can only be used once.
            </p>
          </CardContent>
        </Card>

        {/* Invite Tokens List */}
        <Card className="rounded-xl border bg-card text-card-foreground shadow">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Invite Tokens
            </CardTitle>
            <CardDescription>
              Manage existing invite tokens. Revoke active tokens to invalidate them.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {inviteTokens.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Link
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Expires
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {inviteTokens.map((token) => {
                      const status = getStatus(token);
                      const inviteUrl = getInviteUrl(token.token);
                      const daysUntilExpiry = getDaysUntilExpiry(token.expires_at);

                      return (
                        <tr key={token.id}>
                          <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                            <div className="flex flex-col">
                              <span>{token.name}</span>
                              {token.used && token.user_id && (
                                <span className="text-xs text-muted-foreground">
                                  User ID: {token.user_id}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center gap-2">
                              <code className="text-xs bg-muted px-2 py-1 rounded truncate max-w-[200px]">
                                {inviteUrl}
                              </code>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => copyToClipboard(inviteUrl, token.id)}
                                title="Copy link to clipboard"
                              >
                                {copyStatus[token.id] === 'success' ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : copyStatus[token.id] === 'error' ? (
                                  <X className="h-4 w-4 text-red-600" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                status === 'active'
                                  ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                                  : status === 'used'
                                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
                                  : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                              }`}
                            >
                              {status === 'active' && daysUntilExpiry > 0 && (
                                <Clock className="h-3 w-3 mr-1" />
                              )}
                              {status === 'active'
                                ? `Active (${daysUntilExpiry}d left)`
                                : status === 'used'
                                ? 'Used'
                                : 'Expired'}
                            </span>
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {formatDate(token.created_at)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {formatDate(token.expires_at)}
                          </td>
                          <td className="px-4 py-4 whitespace-nowrap text-sm">
                            {status === 'active' ? (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRevokeToken(token.id)}
                                className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Revoke
                              </Button>
                            ) : status === 'used' && token.user_id ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                                Used
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Expired
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <LinkIcon className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-1">No invite links yet</h3>
                <p className="text-sm text-muted-foreground">
                  Create your first invite link above to get started.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Index;
